// The "What Ritona did" ACTIVITY RAIL (Today, brief §4: a sticky rail with INTERNAL scroll), in the
// prototype's own feed vocabulary (.feed-scroll/.feed-item: a mono when-column, the what line, the muted
// outcome line). It renders the sanitised, hashed-identity Audit projection the API already resolved to
// display names (G-058) — the rail adds nothing and derives nothing beyond display-only time formatting
// (CR-INT-8). The scroll lives on the rail's own region (prototype .feed-scroll) so the rest of the page
// does not move with it. An empty rail states its emptiness honestly (P-SELF). Tokens only (G-181).

import type { ActivityRow } from '../projections.ts';
import { EmptyState } from '../primitives.tsx';

/** Format an ISO timestamp as the prototype's short mono when-column (HH:MM local); pass through anything
 *  that does not parse (display-only formatting — never a derived fact). */
function shortWhen(timestamp: string): string {
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return timestamp;
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Shorten a long opaque ref for the rail (display-only): `usr_8110cf…5e933` stays identifiable and
 *  scannable without wrapping the row (deep-sweep 2026-07-09 F8). Short refs pass through unchanged;
 *  resolution to display NAMES is the owning compose's job when it grows a directory port. */
function shortRef(ref: string): string {
  if (ref.length <= 18) return ref;
  return `${ref.slice(0, 10)}…${ref.slice(-4)}`;
}

export interface ActivityRailProps {
  readonly rows: readonly ActivityRow[];
}

/** The Today activity rail — internal-scroll feed of the sanitised Audit projection. */
export function ActivityRail({ rows }: ActivityRailProps): React.JSX.Element {
  if (rows.length === 0) {
    return <EmptyState>No recent activity to show.</EmptyState>;
  }
  return (
    <div className="feed-scroll">
      <ol aria-label="Activity feed" className="m-0 list-none p-0">
        {rows.map((row) => (
          <li key={row.id} className="feed-item">
            <div className="when">{shortWhen(row.timestamp)}</div>
            <div>
              <div className="what">{row.capability}</div>
              <div className="out">
                {row.outcome}
                {row.objectRef !== null ? ` · ${shortRef(row.objectRef)}` : ''}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
